import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
  Drawer,
  ListItemButton,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import Chart from "../charts/chart";
import ToastComponent, {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../components/ToastComponent";
import levenshtein from "fast-levenshtein";
// import Select from "../../../components/bootstrap/forms/Select";
import HistoryIcon from "@mui/icons-material/History";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
// import EditIcon from "@mui/icons-material/Edit";
import {
  CustomPerson,
  SendTextIcon,
  RightArrow,
} from "../../../components/icon/svg-icons";

interface ChartInstanceData {
  status: string;
  message: string;
  obj: any[] | any;
}

interface Message {
  text?: string;
  sender: "user" | "bot";
  timestamp: Date;
  chartDisplayConfig?: typeof defaultChartData;
  chartInstanceData?: ChartInstanceData;
  rawQuery?: string;
  viewType?: string;
  dataSourceIdUsed?: string;
  originalQuestion?: string;
}

const defaultChartData = {
  viewType: "Grid",
  WidgetData: undefined,
};

const VyuGPT: React.FC = () => {
  const vyu_api_url = process.env.REACT_APP_VYU_API_URL;
  const vyu_service_url = process.env.REACT_APP_VYU_SERVICE_URL;

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [storediInput, setStoredInput] = useState("");
  const [dataSources, setDataSources] = useState<any[]>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(
    null
  );
  const [showDataSourcePrompt, setShowDataSourcePrompt] = useState(false);
  const [viewTypeOptions, setViewTypeOptions] = React.useState<any[]>([]);
  const [selectedViewType, setSelectedViewType] = useState<string>("Grid");
  const [selectedViewTypeId, setSelectedViewTypeId] = useState<string | null>(
    null
  );
  // const [promptType, setPromptType] = useState("Prompt");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  // const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // const [editedText, setEditedText] = useState<string>("");
  // const chipData = [
  //   "Customers",
  //   "Rewards",
  //   "Products",
  //   "Offers",
  //   "Stores",
  //   "Sales",
  //   "Activity",
  // ];
  const [chipData, setChipData] = useState<any[]>([]);
  const drawerWidth = 250;

  const scrollablePaperRef = useRef<null | HTMLDivElement>(null);

  const token: string | null = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const isFuzzyMatch = (inputText: string, keywords: string[]): boolean => {
    const userWords = inputText.toLowerCase().split(/\s+/);

    // Handle multi-word phrases first (e.g., "data source")
    for (const keyword of keywords) {
      if (keyword.includes(" ") && inputText.toLowerCase().includes(keyword)) {
        return true;
      }
    }

    const singleWordKeywords = keywords.filter((k) => !k.includes(" "));

    for (const userWord of userWords) {
      for (const keyword of singleWordKeywords) {
        const threshold = keyword.length <= 4 ? 0 : keyword.length <= 9 ? 1 : 2;

        // CHANGED: Call the library function instead of your local one.
        const distance = levenshtein.get(userWord, keyword);

        if (distance <= threshold) {
          return true;
        }
      }
    }

    return false;
  };

  React.useEffect(() => {
    // console.log("messages in useEffect:", messages);

    apiCallToGetAllDataSources();
    fetchViewTypeData();
    historyApiCall(true);
  }, []);

  useEffect(() => {
    if (scrollablePaperRef.current) {
      scrollablePaperRef.current.scrollTop =
        scrollablePaperRef.current.scrollHeight;
    }
  }, [messages]);

  const handleWarning = (message: string) => {
    showWarningToast(message);
  };
  const handleSuccess = (message: string) => {
    showSuccessToast(message);
  };
  const handleError = (message: string) => {
    showErrorToast(message);
  };

  const helpPromptApiCall = async (initialLoad = false) => {
    try {
      const response = await fetch(`${vyu_service_url}/help-prompt`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch data sources: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.status === "Success" && data.obj) {
        console.log("IN HELP PROMPT RESPONSE:", data);

        const helpText = data.obj;

        const regex = /areas like ([\s\S]*?), with important fields like/;
        const match = helpText.match(regex);

        if (match && match[1]) {
          const extractedData = match[1]
            .split(",")
            .map((item: any) => item.trim())
            .filter(Boolean);

          if (extractedData.length > 0) {
            console.log("Chip data from helpPromptApiCall", extractedData);

            setChipData(extractedData);
          }
        }
        const botHelpMessage: Message = {
          text: data.obj,
          sender: "bot",
          timestamp: new Date(),
        };

        setMessages([botHelpMessage]);
      } else {
        handleError(
          data.message || "An unknown error occurred fetching help prompt."
        );
      }
    } catch (error) {
      console.error("Error fetching vYu GPT help prompt:", error);
    }
  };

  const transformHistoryToMessages = (historyItems: any[]): Message[] => {
    console.log("Entered transformHistoryToMessages");

    if (!historyItems || historyItems.length === 0) return [];
    return historyItems.flatMap((item): Message[] => {
      const historyDate = new Date(item.timestamp);
      const responseData = item.response;
      const userMessage: Message = {
        text: item.question,
        sender: "user",
        timestamp: historyDate,
      };
      let botMessage: Message;
      console.log(
        "Response in transformHistoryToMessages",
        responseData.previewResult.obj
      );

      if (
        responseData?.previewResult?.status === "Success" &&
        responseData?.previewResult?.obj.length === 0
      ) {
        botMessage = {
          text:
            responseData?.previewResult?.message ||
            responseData?.message ||
            "Could not reconstruct response from history.",
          sender: "bot",
          timestamp: historyDate,
          rawQuery: responseData?.rawQuery,
          dataSourceIdUsed: String(responseData.datasourceId),
          originalQuestion: item.question,
        };
      } else {
        botMessage = {
          sender: "bot",
          timestamp: historyDate,
          chartDisplayConfig: defaultChartData,
          chartInstanceData: {
            status: responseData.previewResult.status,
            message: responseData.previewResult.message,
            obj: responseData.previewResult.obj,
          },
          rawQuery: responseData.rawQuery,
          viewType: "Grid",
          dataSourceIdUsed: String(responseData.datasourceId),
          originalQuestion: item.question,
        };
      }
      return [userMessage, botMessage];
    });
  };

  const historyApiCall = async (initialLoad = false) => {
    try {
      const response = await fetch(`${vyu_service_url}/gptHistory?days=${30}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch data sources: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.status === "success") {
        setChatHistory(data.obj || []);
        console.log("object length");

        if (data.obj.length > 0) {
          const lastItem = data.obj[data.obj.length - 1];
          const lastDatasourceId = lastItem?.response?.datasourceId;
          console.log("Last datasourceId:", lastDatasourceId);
          setSelectedDataSource(lastDatasourceId);
        }
        const historyItems = data.obj || [];
        if (historyItems.length > 0) {
          const lastItem = historyItems[historyItems.length - 1];
          setSelectedDataSource(lastItem?.response?.datasourceId);
          if (initialLoad) {
            const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
            const recentHistory = historyItems.filter(
              (item: any) => new Date(item.timestamp) > oneHourAgo
            );
            if (recentHistory.length > 0) {
              const initialMessages = transformHistoryToMessages(recentHistory);
              setMessages(initialMessages);
            } else {
              helpPromptApiCall();
            }
          }
        } else {
          helpPromptApiCall();
        }
      } else {
        handleError(
          data.message || "An unknown error occurred fetching history."
        );
        setChatHistory([]);
        helpPromptApiCall();
      }
    } catch (error) {
      console.error("Error fetching vYu GPT history:", error);
      setChatHistory([]);
    }
  };

  const handleCopyToClipboard = async (textToCopy: string) => {
    if (!textToCopy) return;
    try {
      await navigator.clipboard.writeText(textToCopy);
      handleSuccess("Prompt copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
      handleError("Could not copy text.");
    }
  };

  const apiCallToGetAllDataSources = async () => {
    try {
      const response = await fetch(
        `${vyu_api_url}/datasource/getAllDatasource`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch data sources: ${response.statusText}`);
      }
      const data = await response.json();
      setDataSources(data || []);
    } catch (error) {
      console.error("Error fetching data sources:", error);
      setDataSources([]);
    }
  };

  const fetchViewTypeData = async () => {
    try {
      const response = await fetch(`${vyu_api_url}/viewType/getAllViewType`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch view types");
      }
      const data = await response.json();
      const fetchedViewTypeOptions = data.obj || [];
      setViewTypeOptions(fetchedViewTypeOptions);

      if (fetchedViewTypeOptions.length > 0) {
        const defaultView = fetchedViewTypeOptions.find(
          (vt: any) => vt.name === selectedViewType
        );
        if (defaultView) {
          setSelectedViewTypeId(defaultView.id);
        } else if (fetchedViewTypeOptions.length > 0) {
          console.warn(
            `Default view type "${selectedViewType}" not found in fetched options.`
          );
        }
      }
    } catch (error) {
      console.error("Error fetching view type data:", error);
      setViewTypeOptions([]);
    }
  };

  const fetchBotResponse = async (
    question: string,
    dataSourceId: string | null
  ) => {
    try {
      setShowDataSourcePrompt(false);
      const contextValue = "vyuGpt";
      const response = await fetch(`${vyu_service_url}/generateSqlQuery`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          question: question,
          datasourceId: dataSourceId,
          context: contextValue,
          // promptType: promptType,
        }),
      });
      console.log("response  in generateSqlQuery", response);
      const data = await response.json();
      console.log("response data in generateSqlQuery", data);

      // if (data.status === "Failure" && data.obj === null) {
      //   handleError(data.message);
      //   setShowDataSourcePrompt(true);
      //   return;
      // }
      if (
        data.message ===
        "You have access to multiple data sources. Please choose one to continue."
      ) {
        handleError(data.message);
        setShowDataSourcePrompt(true);
        return;
      }
      const currentRawQuery = data.obj?.rawQuery || "No query available";

      const responseDataSourceId = data.obj?.datasourceId || dataSourceId;
      setSelectedDataSource(responseDataSourceId);

      if (
        data.obj?.previewResult?.status === "Success" &&
        data.obj?.previewResult?.obj.length === 0
      ) {
        const botErrorMessage: Message = {
          text: `${data.obj.previewResult.message}`,
          sender: "bot",
          timestamp: new Date(),
          rawQuery: currentRawQuery,
          dataSourceIdUsed: responseDataSourceId, // CHANGED: Store the ID with the message
          originalQuestion: question,
        };
        setMessages((prevMessages) => [...prevMessages, botErrorMessage]);
        handleSuccess(data.obj.previewResult.message);
        return;
      } else if (
        data.status === "success" &&
        data.obj?.previewResult.status === "Success"
      ) {
        handleSuccess(data.message);
      }
      if (!response.ok) {
        handleError(data.obj?.previewResult.message);
        const errorText =
          data?.message ||
          response.statusText ||
          `HTTP error! status: ${response.status}`;
        throw new Error(errorText);
      }

      const jsonResponse: ChartInstanceData | undefined =
        data.obj?.previewResult;

      if (jsonResponse && jsonResponse.status === "FAILED") {
        const botErrorMessage: Message = {
          text: `Error: ${jsonResponse.message}`,
          sender: "bot",
          timestamp: new Date(),
          rawQuery: currentRawQuery,
          dataSourceIdUsed: responseDataSourceId,
          originalQuestion: question,
        };
        setMessages((prevMessages) => [...prevMessages, botErrorMessage]);
      } else if (
        jsonResponse &&
        jsonResponse.obj &&
        jsonResponse.status &&
        jsonResponse.message
      ) {
        const botChartMessage: Message = {
          sender: "bot",
          timestamp: new Date(),
          chartDisplayConfig: defaultChartData,
          chartInstanceData: {
            status: jsonResponse.status,
            message: jsonResponse.message,
            obj: jsonResponse.obj,
          },
          rawQuery: currentRawQuery,
          viewType: selectedViewType || "Grid",
          dataSourceIdUsed: responseDataSourceId, // CHANGED: Store the ID with the message
          originalQuestion: question,
        };
        setMessages((prevMessages) => [...prevMessages, botChartMessage]);
      } else {
        handleError(data.obj?.previewResult.message);
        const botResponseMessage: Message = {
          text:
            data.obj?.previewResult.message ||
            "Received a response, but chart data is not in the expected format.",
          sender: "bot",
          timestamp: new Date(),
          rawQuery: currentRawQuery,
          dataSourceIdUsed: responseDataSourceId,
          originalQuestion: question,
        };
        setMessages((prevMessages) => [...prevMessages, botResponseMessage]);
      }
      historyApiCall();
    } catch (error: any) {
      console.error("Error sending message or fetching data:", error);
      const botErrorMessage: Message = {
        text: `Sorry, an error occurred: ${error.message || "Could not connect to the server."}`,
        sender: "bot",
        timestamp: new Date(),
        originalQuestion: question,
        dataSourceIdUsed: dataSourceId || undefined, // CHANGED: Store the ID even on error
      };
      setMessages((prevMessages) => [...prevMessages, botErrorMessage]);
    }
  };

  const handleSend = async (dataSourceIdParam?: string | null) => {
    const currentQuestion = storediInput;
    console.log("currentQuestion in handle send:", currentQuestion);

    if (!currentQuestion.trim()) {
      return;
    }
    const actionKeywords = [
      "switch",
      "change",
      "choose",
      "select",
      "pick",
      "use",
      "get",
      "list",
      "show",
      "different",
      "another",
    ];
    const subjectKeywords = ["datasource", "data source"];

    const lowerCaseQuestion = currentQuestion.toLowerCase();

    const hasActionKeyword = isFuzzyMatch(lowerCaseQuestion, actionKeywords);
    const hasSubjectKeyword = isFuzzyMatch(lowerCaseQuestion, subjectKeywords);

    const isSwitchRequest = hasActionKeyword && hasSubjectKeyword;
    if (isSwitchRequest && currentQuestion) {
      const botPromptMessage: Message = {
        text: "Please choose a data source from the list below.",
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prevMessages) => [...prevMessages, botPromptMessage]);
      setShowDataSourcePrompt(true);
      setStoredInput("");
      setInput("");
      return;
    }

    const userMessage: Message = {
      text: currentQuestion,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput("");

    // This is the data source ID that will be used for THIS specific request.
    const finalDataSourceId = dataSourceIdParam || selectedDataSource;

    if (!finalDataSourceId) {
      console.warn(
        "Attempting to send without a dataSourceId explicitly selected or passed."
      );
    }
    await fetchBotResponse(currentQuestion, finalDataSourceId);
  };

  const handleCreateWidget = async (message: Message) => {
    const question = message.originalQuestion;
    const rawQuery = message.rawQuery;

    const dataSourceIdForWidget = message.dataSourceIdUsed;

    if (!dataSourceIdForWidget) {
      // console.error(
      //   "Cannot create widget: The data source ID is missing from the message context."
      // );
      handleError(
        "Could not create widget because the original data source is unknown."
      );
      return;
    }
    const viewTypeNameFromMessage = message.viewType;

    let derivedViewTypeId = null;
    if (viewTypeNameFromMessage && viewTypeOptions.length > 0) {
      const foundViewType = viewTypeOptions.find(
        (vt: any) => vt.name === viewTypeNameFromMessage
      );
      if (foundViewType) {
        derivedViewTypeId = foundViewType.id;
      } else {
        console.warn(
          `View type name "${viewTypeNameFromMessage}" not found in options. Cannot derive ID.`
        );
      }
    } else if (!viewTypeNameFromMessage) {
      console.warn("Message does not have a viewType. Cannot derive ID.");
    } else {
      console.warn("ViewType options not loaded. Cannot derive ID.");
    }

    try {
      const response = await fetch(`${vyu_api_url}/widget/create`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          question: question,
          datasourceId: dataSourceIdForWidget,
          rawQuery: rawQuery,
          viewTypeId: derivedViewTypeId,
        }),
      });
      const data = await response.json();

      if (data.status === "success") {
        handleSuccess(data.message);
      }
      if (!response.ok) {
        handleError(data.message);
        const errorText =
          data?.message ||
          response.statusText ||
          `HTTP error! status: ${response.status}`;
        throw new Error(errorText);
      }
    } catch (error: any) {
      console.error("Error creating widget:", error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend(selectedDataSource);
    }
  };

  const handleDataSourceSelect = (dataSourceId: string) => {
    if (dataSourceId) {
      setSelectedDataSource(dataSourceId);
      setShowDataSourcePrompt(false);
      if (storediInput.trim()) {
        handleSend(dataSourceId);
      }
    }
  };

  const handleGlobalViewTypeChange = (viewTypeName: string) => {
    setSelectedViewType(viewTypeName);
    const foundViewType = viewTypeOptions.find(
      (vt: any) => vt.name === viewTypeName
    );
    if (foundViewType) {
      setSelectedViewTypeId(foundViewType.id);
    } else {
      setSelectedViewTypeId(null);
      console.warn(
        `View type name "${viewTypeName}" not found in options during global change.`
      );
    }
  };

  const handleMessageSpecificViewTypeChange = (
    messageIndex: number,
    newViewTypeName: string
  ) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg, idx) => {
        if (idx === messageIndex && msg.chartDisplayConfig) {
          return { ...msg, viewType: newViewTypeName };
        }
        return msg;
      })
    );
    // console.log("In handleMessageSpecificViewTypeChange", messages);
    handleGlobalViewTypeChange(newViewTypeName);
  };

  // const handleHistoryItemClick = (item: any) => {
  //   const ts = item.timestamp;
  //   // JS month is 0-indexed, so we subtract 1
  //   const historyDate = new Date(item.timestamp);
  //   console.log("In handleHistoryItemClick", item);

  //   const userMessage: Message = {
  //     text: item.question,
  //     sender: "user",
  //     timestamp: historyDate,
  //   };

  //   const responseData = item.response;
  //   let botMessage: Message;

  //   if (
  //     item.response.previewResult &&
  //     item.response.previewResult.status === "Success" &&
  //     item.response.previewResult.obj
  //   ) {
  //     botMessage = {
  //       sender: "bot",
  //       timestamp: historyDate,
  //       chartDisplayConfig: defaultChartData,
  //       chartInstanceData: {
  //         status: item.response.previewResult.status,
  //         message: item.response.previewResult.message,
  //         obj: item.response.previewResult.obj,
  //       },
  //       rawQuery: item.response.rawQuery,
  //       viewType: "Grid",
  //       dataSourceIdUsed: String(item.response.datasourceId),
  //       originalQuestion: item.question,
  //     };
  //   } else {
  //     botMessage = {
  //       text:
  //         item.response.previewResult?.message ||
  //         "Could not reconstruct response from history.",
  //       sender: "bot",
  //       timestamp: historyDate,
  //       rawQuery: responseData.rawQuery,
  //       dataSourceIdUsed: String(responseData.datasourceId),
  //       originalQuestion: item.question,
  //     };
  //   }

  //   setMessages([userMessage, botMessage]);
  //   setSelectedDataSource(String(item.response.datasourceId));
  //   setInput("");
  //   setStoredInput("");
  //   setShowDataSourcePrompt(false);
  //   // setIsHistoryOpen(false);
  // };

  const handleHistoryItemClick = (item: any) => {
    const [userMessage, botMessage] = transformHistoryToMessages([item]);
    setMessages((prev) => [...prev, userMessage, botMessage]);
    setSelectedDataSource(String(item.response.datasourceId));
    setInput(item.question);
    setStoredInput(item.question);
    setShowDataSourcePrompt(false);
  };

  const getSelectedDataSourceName = () => {
    if (!selectedDataSource || !dataSources.length) {
      return "Not Selected";
    }
    const dataSource = dataSources.find(
      (ds) => String(ds.id) === String(selectedDataSource)
    );
    return dataSource ? dataSource.name : "Unknown Source";
  };
  // const handleEditSave = (index: any, editedText: any) => {
  //   console.log("Datasource ID in handleEditSave:", selectedDataSource);
  //   setStoredInput(editedText);
  //   console.log("Index in handleEditSave:", index);
  //   console.log("EditedText in handleEditSave:", editedText);
  //   // handleSend(selectedDataSource);
  //   fetchBotResponse(editedText, selectedDataSource);
  // };
  const handleChipClick = async (chipLabel: string) => {
    const prompt = `select * from ${chipLabel}`;
    setInput(prompt);
    setStoredInput(prompt);
  };
  const handleChipDelete = (chipToDelete: string) => {
    // Filter out the chip that needs to be removed
    const updatedChipData = chipData.filter((chip) => chip !== chipToDelete);
    // Update the state with the new array
    setChipData(updatedChipData);
  };

  return (
    <Box sx={{ display: "flex", position: "relative" }}>
      <Box
        sx={{
          height: "calc(100vh - 150px)",
          display: "flex",
          flexDirection: "column",
          p: 2,
          flexGrow: 1,
          transition: "margin-right .2s",
          marginRight: isHistoryOpen ? `${drawerWidth}px` : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            // mb: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
              backgroundColor: "action.hover",
              px: 5.5,
              py: 1.5,
              borderRadius: "16px",
            }}
          >
            Current Datasource: {getSelectedDataSourceName()}
          </Typography>
          <IconButton
            onClick={() => {
              setIsHistoryOpen(true);
              // historyApiCall();
            }}
            sx={{
              alignSelf: "flex-end",
              border: "1px solid",
              borderColor: "#0D57DE",
              backgroundColor: "#0D57DE",
              color: "white",
              width: 40,
              height: 40,
              "&:hover": {
                backgroundColor: "#0D57DE",
              },
              ...(isHistoryOpen && { display: "none" }),
            }}
            title="View History"
          >
            <HistoryIcon />
          </IconButton>
        </Box>
        <Paper
          ref={scrollablePaperRef}
          elevation={0}
          sx={{
            flex: 1,
            mb: 2,
            p: 2,
            overflow: "auto",
            backgroundColor: "background.default",
            borderRadius: 2,
          }}
        >
          <List>
            {messages.map((message, index) => (
              <React.Fragment key={index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection:
                      message.sender === "user" ? "row-reverse" : "row",
                    gap: 1,
                  }}
                >
                  <ListItemAvatar>
                    {/* <Avatar
                      sx={{
                        bgcolor:
                          message.sender === "user"
                            ? "primary.main"
                            : "secondary.main",
                      }}
                    >
                      {message.sender === "user" ? (
                        <svg
                          width="96"
                          height="100"
                          viewBox="0 0 96 100"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="28.5"
                            y="0.5"
                            width="39"
                            height="39"
                            rx="19.5"
                            stroke="#E2E8F0"
                          />
                          <g filter="url(#filter0_d_42_5291)">
                            <path
                              d="M48 19.0461C47.099 19.0461 46.3577 18.7622 45.7762 18.1944C45.1946 17.6266 44.9039 16.9028 44.9039 16.023C44.9039 15.1375 45.1946 14.4122 45.7762 13.8473C46.3577 13.2824 47.099 13 48 13C48.901 13 49.6423 13.2824 50.2238 13.8473C50.8054 14.4122 51.0961 15.1375 51.0961 16.023C51.0961 16.9028 50.8054 17.6266 50.2238 18.1944C49.6423 18.7622 48.901 19.0461 48 19.0461ZM42.0769 26C41.7778 26 41.5235 25.8978 41.3141 25.6933C41.1047 25.4888 41 25.2406 41 24.9485V24.2703C41 23.7808 41.1328 23.3543 41.3985 22.991C41.6641 22.6276 42.0093 22.3484 42.4341 22.1533C43.4165 21.7269 44.3636 21.4056 45.2754 21.1894C46.1872 20.9733 47.0954 20.8652 48 20.8652C48.9046 20.8652 49.8104 20.9756 50.7174 21.1964C51.6244 21.4172 52.5691 21.7362 53.5515 22.1533C53.9859 22.3484 54.3359 22.6276 54.6015 22.991C54.8672 23.3543 55 23.7808 55 24.2703V24.9485C55 25.2406 54.8953 25.4888 54.6859 25.6933C54.4765 25.8978 54.2222 26 53.9231 26H42.0769Z"
                              fill="#0D57DE"
                            />
                          </g>
                          <defs>
                            <filter
                              id="filter0_d_42_5291"
                              x="0.0499992"
                              y="4.55"
                              width="95.9"
                              height="94.9"
                              filterUnits="userSpaceOnUse"
                              color-interpolation-filters="sRGB"
                            >
                              <feFlood
                                flood-opacity="0"
                                result="BackgroundImageFix"
                              />
                              <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                              />
                              <feOffset dy="32.5" />
                              <feGaussianBlur stdDeviation="20.475" />
                              <feComposite in2="hardAlpha" operator="out" />
                              <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0.262745 0 0 0 0 0.0941176 0 0 0 0 1 0 0 0 0.28 0"
                              />
                              <feBlend
                                mode="normal"
                                in2="BackgroundImageFix"
                                result="effect1_dropShadow_42_5291"
                              />
                              <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="effect1_dropShadow_42_5291"
                                result="shape"
                              />
                            </filter>
                          </defs>
                        </svg>
                      ) : (
                        <svg
                          width="96"
                          height="100"
                          viewBox="0 0 96 100"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="28.5"
                            y="0.5"
                            width="39"
                            height="39"
                            rx="19.5"
                            stroke="#E2E8F0"
                          />
                          <g filter="url(#filter0_d_42_5291)">
                            <path
                              d="M48 19.0461C47.099 19.0461 46.3577 18.7622 45.7762 18.1944C45.1946 17.6266 44.9039 16.9028 44.9039 16.023C44.9039 15.1375 45.1946 14.4122 45.7762 13.8473C46.3577 13.2824 47.099 13 48 13C48.901 13 49.6423 13.2824 50.2238 13.8473C50.8054 14.4122 51.0961 15.1375 51.0961 16.023C51.0961 16.9028 50.8054 17.6266 50.2238 18.1944C49.6423 18.7622 48.901 19.0461 48 19.0461ZM42.0769 26C41.7778 26 41.5235 25.8978 41.3141 25.6933C41.1047 25.4888 41 25.2406 41 24.9485V24.2703C41 23.7808 41.1328 23.3543 41.3985 22.991C41.6641 22.6276 42.0093 22.3484 42.4341 22.1533C43.4165 21.7269 44.3636 21.4056 45.2754 21.1894C46.1872 20.9733 47.0954 20.8652 48 20.8652C48.9046 20.8652 49.8104 20.9756 50.7174 21.1964C51.6244 21.4172 52.5691 21.7362 53.5515 22.1533C53.9859 22.3484 54.3359 22.6276 54.6015 22.991C54.8672 23.3543 55 23.7808 55 24.2703V24.9485C55 25.2406 54.8953 25.4888 54.6859 25.6933C54.4765 25.8978 54.2222 26 53.9231 26H42.0769Z"
                              fill="#0D57DE"
                            />
                          </g>
                          <defs>
                            <filter
                              id="filter0_d_42_5291"
                              x="0.0499992"
                              y="4.55"
                              width="95.9"
                              height="94.9"
                              filterUnits="userSpaceOnUse"
                              color-interpolation-filters="sRGB"
                            >
                              <feFlood
                                flood-opacity="0"
                                result="BackgroundImageFix"
                              />
                              <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                              />
                              <feOffset dy="32.5" />
                              <feGaussianBlur stdDeviation="20.475" />
                              <feComposite in2="hardAlpha" operator="out" />
                              <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0.262745 0 0 0 0 0.0941176 0 0 0 0 1 0 0 0 0.28 0"
                              />
                              <feBlend
                                mode="normal"
                                in2="BackgroundImageFix"
                                result="effect1_dropShadow_42_5291"
                              />
                              <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="effect1_dropShadow_42_5291"
                                result="shape"
                              />
                            </filter>
                          </defs>
                        </svg>
                      )}
                    </Avatar> */}
                    <CustomPerson />
                  </ListItemAvatar>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 1.5,
                      maxWidth: "70%",
                      backgroundColor: "transparent",
                      color: "black",
                      borderRadius: 1,
                      border: "1px solid #ccc",
                    }}
                  >
                    {message.chartDisplayConfig && message.chartInstanceData ? (
                      <Box width={750} height="auto">
                        {message.rawQuery && (
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontFamily: "monospace",
                              backgroundColor: "rgba(0,0,0,0.03)",
                              p: 1,
                              borderRadius: 1,
                            }}
                          >
                            {message.rawQuery}
                          </Typography>
                        )}
                        <Box sx={{ width: 750, height: 500 }}>
                          <Chart
                            key={message.timestamp.getTime()}
                            viewType={message.viewType || "Grid"}
                            widgetData={message.chartDisplayConfig.WidgetData}
                            // chartData={message.chartInstanceData}
                            chartData={JSON.parse(
                              JSON.stringify(message.chartInstanceData)
                            )}
                            timeOut={1000}
                            print={undefined}
                            parentCallbackPrint={() => {}}
                          />
                        </Box>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          mt={1.5}
                          gap={1.5}
                          flexWrap="wrap"
                        >
                          <Button
                            variant="contained"
                            color="secondary"
                            size="medium"
                            onClick={() => handleCreateWidget(message)}
                          >
                            Create Widget
                          </Button>
                          <TextField
                            select
                            value={message.viewType || "Grid"}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value)
                                handleMessageSpecificViewTypeChange(
                                  index,
                                  value
                                );
                            }}
                            SelectProps={{ native: true }}
                            variant="outlined"
                            size="small"
                            sx={{ width: "auto", minWidth: "150px" }}
                          >
                            <option value="" disabled>
                              -- Select View Type --
                            </option>
                            {viewTypeOptions.map((option) => (
                              <option key={option.id} value={option.name}>
                                {option.name}
                              </option>
                            ))}
                          </TextField>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            display: "block",
                            textAlign:
                              message.sender === "user" ? "right" : "left",
                            mt: 0.25,
                          }}
                        >
                          {message.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    ) : (
                      // : editingIndex === index ? (
                      //   <TextField
                      //     fullWidth
                      //     multiline
                      //     value={editedText}
                      //     onChange={(e) => setEditedText(e.target.value)}
                      //     onBlur={() => {
                      //       handleEditSave(index, editedText);
                      //       setEditingIndex(null);
                      //     }}
                      //     onKeyDown={(e) => {
                      //       if (e.key === "Enter" && !e.shiftKey) {
                      //         e.preventDefault();
                      //         handleEditSave(index, editedText);
                      //         setEditingIndex(null);
                      //       }
                      //     }}
                      //     autoFocus
                      //     variant="outlined"
                      //     size="small"
                      //     sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      //   />
                      // )
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {message.text}
                      </Typography>
                    )}

                    {message.rawQuery &&
                      !(
                        message.chartDisplayConfig && message.chartInstanceData
                      ) && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{
                            padding: "4px 8px",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.secondary",
                            backgroundColor: "rgba(0,0,0,0.03)",
                            borderRadius: 1,
                            mt: 0.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {message.rawQuery}
                        </Typography>
                      )}

                    {message.sender === "user" && message.text && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 0.25,
                        }}
                      >
                        <IconButton
                          className="copy-button"
                          size="small"
                          title="Copy prompt"
                          onClick={() =>
                            handleCopyToClipboard(message.text || "")
                          }
                          sx={{
                            alignSelf: "flex-start",
                            // padding: "1px 1px",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.secondary",
                            backgroundColor: "rgba(0,0,0,0.03)",
                            borderRadius: 1,
                            mt: 0.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                        {/* <IconButton
                          className="copy-button"
                          size="small"
                          title="Edit prompt"
                          onClick={() => {
                            setEditingIndex(index);
                            setEditedText(message.text || "");
                          }}
                          sx={{
                            alignSelf: "flex-start",
                            // padding: "1px 1px",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "text.secondary",
                            backgroundColor: "rgba(0,0,0,0.03)",
                            borderRadius: 1,
                            mt: 0.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton> */}

                        <Typography
                          variant="caption"
                          sx={{
                            opacity: 0.7,
                            display: "block",
                            textAlign:
                              message.sender === "user" ? "right" : "left",
                            mt: 0.25,
                          }}
                        >
                          {message.timestamp.toLocaleTimeString()}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </ListItem>
                {index < messages.length - 1 && (
                  <Divider
                    variant="inset"
                    component="li"
                    sx={{
                      ml: message.sender === "bot" ? "72px" : "auto",
                      mr: message.sender === "user" ? "72px" : "auto",
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {showDataSourcePrompt && (
              <ListItem alignItems="flex-start">
                <ListItemAvatar>
                  {/* <Avatar sx={{ bgcolor: "secondary.main" }}>
                    <SmartToyIcon />
                  </Avatar> */}
                  <CustomPerson />
                </ListItemAvatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: "70%",
                    backgroundColor: "transparent",
                    color: "text.primary",
                    borderRadius: 2,
                    border: "1px solid #ccc",
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Please select a data source to continue:
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={selectedDataSource || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) handleDataSourceSelect(value);
                    }}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    autoFocus
                  >
                    <option value="" disabled>
                      -- Select Data Source --
                    </option>
                    {dataSources.map((source) => (
                      <option key={source.id} value={source.id}>
                        {source.name}
                      </option>
                    ))}
                  </TextField>
                </Paper>
              </ListItem>
            )}
          </List>
        </Paper>

        <Box
          sx={{
            // This is the main container card
            backgroundColor: "white",
            borderRadius: 6,
            border: "1px solid",
            borderColor: "grey.200",
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setStoredInput(e.target.value);
              }}
              onKeyPress={handleKeyPress}
              placeholder="Ask or search anything..."
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <IconButton
              color="primary"
              onClick={() => handleSend(selectedDataSource)}
              disabled={!input.trim()}
            >
              <SendTextIcon />
            </IconButton>
          </Box>
          {chipData.length > 0 && (
            <Box
              sx={{
                backgroundColor: "grey.100",
                borderRadius: 8,
                p: 1.5,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {chipData.map((item) => (
                  <Chip
                    key={item}
                    label={item}
                    onClick={() => handleChipClick(item)}
                    onDelete={() => handleChipDelete(item)}
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "10px",
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Drawer
        sx={{
          // width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            height: "calc(100vh - 150px)",
            top: "auto",
          },
        }}
        variant="persistent"
        anchor="right"
        open={isHistoryOpen}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ ml: 1 }}>
            History
          </Typography>
          <IconButton onClick={() => setIsHistoryOpen(false)}>
            {/* <ChevronRightIcon /> */}
            <RightArrow />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ overflowY: "auto" }}>
          {chatHistory.length > 0 ? (
            chatHistory.map((item, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton onClick={() => handleHistoryItemClick(item)}>
                  <ListItemText
                    primary={item.question}
                    primaryTypographyProps={{
                      noWrap: true,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No history found." />
            </ListItem>
          )}
        </List>
      </Drawer>

      <ToastComponent />
    </Box>
  );
};

export default VyuGPT;
